import './Style.scss';
import * as React from 'react';
import { DataPoint, FileInterface } from 'Interfaces/DataPoint';
import { FormInterface } from 'Interfaces/Forms/FormsInterface';
import { QuestionInterface, questionTypes } from 'Interfaces/Forms/QuestionInterface';
import { getOptionTexts, isSelectQuestion } from 'views/SingleInstance/utils/utils';
import { isNullOrUndefined } from 'utils/utils';
import { useAppDispatch } from 'index';
import { fetchPOI } from 'actions/pois';

interface Props {
  form: FormInterface;
  dataPoint: DataPoint;
}

const SKIPPED_TYPES = new Set<string>([
  questionTypes.TABLE_QUESTION,
  questionTypes.PART_OF_QUESTION,
  questionTypes.TASK_QUESTION,
  questionTypes.HEADER_QUESTION,
]);

const isHidden = (qn: QuestionInterface): boolean => {
  return Boolean(qn.hideInPortal || qn.hideInSingleInstance || qn.inVisible || qn.deleted);
};

const filesForQuestion = (dataPoint: DataPoint, questionId: string): FileInterface[] => {
  const files = dataPoint.files || [];
  return files.filter(f => f.questionId === questionId);
};

const getActiveSubQuestions = (qn: QuestionInterface, dataPoint: DataPoint): QuestionInterface[] => {
  const triggers = qn.triggerValues?.triggerValue;
  if (!triggers?.length) {
    return [];
  }
  const raw = dataPoint[qn.id];
  if (isNullOrUndefined(raw) || raw === '') {
    return [];
  }
  const selectedValues = `${raw}`.split(',').map(v => v.trim()).filter(Boolean);
  const active: QuestionInterface[] = [];
  for (const tv of triggers) {
    if (selectedValues.includes(tv.value)) {
      const subs = tv.action?.subQuestions?.question ?? [];
      active.push(...subs);
    }
  }
  return active;
};

const renderValue = (question: QuestionInterface, dataPoint: DataPoint): React.ReactNode => {
  if (question.type === questionTypes.LABEL) {
    return null;
  }

  if (question.type === questionTypes.PICTURES_QUESTION) {
    const pics = filesForQuestion(dataPoint, question.id);
    if (!pics.length) {
      return null;
    }
    return (
      <div className="d-flex flex-wrap">
        {pics.map((file, i) => (
          <img
            key={`${file.id ?? file.fileName}-${i}`}
            src={file.urlThumbnail || file.url}
            alt={file.fileName}
            style={{ maxHeight: 80, marginRight: 8, marginBottom: 8 }}
          />
        ))}
      </div>
    );
  }

  if (question.type === questionTypes.FILE_QUESTION) {
    const docs = filesForQuestion(dataPoint, question.id);
    if (!docs.length) {
      return null;
    }
    return (
      <ul className="list-unstyled mb-0">
        {docs.map((file, i) => (
          <li key={`${file.id ?? file.fileName}-${i}`}>
            {file.url
              ? <a href={file.url} target="_blank" rel="noreferrer">{file.fileName}</a>
              : file.fileName}
          </li>
        ))}
      </ul>
    );
  }

  const raw = dataPoint[question.id];
  if (isNullOrUndefined(raw) || raw === '') {
    return null;
  }

  if (question.type === questionTypes.BOOLEAN_QUESTION) {
    return raw ? 'Yes' : 'No';
  }

  if (isSelectQuestion(question.type)) {
    return getOptionTexts(question, `${raw}`.trim().split(','));
  }

  return String(raw);
};

const renderQuestion = (
  qn: QuestionInterface, dataPoint: DataPoint, depth: number
): React.ReactNode => {
  if (SKIPPED_TYPES.has(qn.type) || isHidden(qn)) {
    return null;
  }

  const style: React.CSSProperties | undefined = depth > 0
    ? { paddingLeft: depth * 16, borderLeft: '2px solid #dee2e6' }
    : undefined;

  let row: React.ReactNode = null;
  if (qn.type === questionTypes.LABEL) {
    row = (
      <div className="mt-2" key={qn.id} data-testid="sub-question-label" style={style}>
        <p className="body-text mb-0">{qn.text}</p>
      </div>
    );
  } else {
    const value = renderValue(qn, dataPoint);
    if (value !== null) {
      row = (
        <div
          className="mt-2"
          key={qn.id}
          data-testid={depth > 0 ? 'sub-question' : 'question'}
          style={style}
        >
          <label className="question-label">{qn.text}</label>
          <div className="question-value">{value}</div>
        </div>
      );
    }
  }

  const subs = getActiveSubQuestions(qn, dataPoint);
  if (!row && !subs.length) {
    return null;
  }

  return (
    <React.Fragment key={qn.id}>
      {row}
      {subs.map(sub => renderQuestion(sub, dataPoint, depth + 1))}
    </React.Fragment>
  );
};

export default function CardView(props: Props) {
  const { form, dataPoint } = props;
  const dispatch = useAppDispatch();
  const [loadedDataPoint, setLoadedDataPoint] = React.useState<DataPoint>(dataPoint);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const abortController = new AbortController();
    const query = dataPoint.id ? { id: dataPoint.id } : undefined;

    if (!query) {
      setLoadedDataPoint(dataPoint);
      return;
    }
    setLoading(true);
    const response = dispatch(
      fetchPOI(form.ref, abortController.signal, undefined, undefined, query)
    );
    response
      .then(res => res.json())
      .then(json => {
        if (Array.isArray(json) && json.length === 1) {
          setLoadedDataPoint({ ...dataPoint, ...json[0] });
        }
      })
      .catch(() => { /* aborted or failed — keep the passed dataPoint */ })
      .finally(() => setLoading(false));

    return () => abortController.abort();
  }, [dispatch, form.ref, dataPoint.id, dataPoint.row_id]);

  return (
    <div className="cards-view p-3" data-testid="cards-view" data-loading={loading}>
      {(form.questionPage || [])
        .filter(page => !page.deleted)
        .flatMap(page => page.question ?? [])
        .map(qn => renderQuestion(qn, loadedDataPoint, 0))}
    </div>
  );
}
